import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;
  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-9 h-9 rounded-full bg-accent-500 flex items-center justify-center">
                <i className="ri-book-open-line text-lg text-background-50"></i>
              </div>
              <span
                className={`text-xl font-bold whitespace-nowrap ${
                  scrolled || !isHome ? 'text-foreground-950' : 'text-background-50'
                }`}
              >
                漫画皮
              </span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive('/')
                    ? 'text-accent-500'
                    : scrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-950'
                    : 'text-background-50/80 hover:text-background-50'
                }`}
              >
                首页
              </Link>

              <div
                className="relative"
                onMouseEnter={() => setCategoryOpen(true)}
                onMouseLeave={() => setCategoryOpen(false)}
              >
                <button
                  className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap ${
                    location.pathname.startsWith('/category')
                      ? 'text-accent-500'
                      : scrolled || !isHome
                      ? 'text-foreground-700 hover:text-foreground-950'
                      : 'text-background-50/80 hover:text-background-50'
                  }`}
                >
                  分类
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </button>
                {categoryOpen && (
                  <div className="absolute top-full left-0 mt-1 w-40 bg-background-50 border border-background-200/70 rounded-lg py-2 shadow-lg">
                    {categories.slice(1).map((cat) => (
                      <button
                        key={cat.slug}
                        onClick={() => {
                          navigate(`/category/${cat.slug}`);
                          setCategoryOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/about"
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive('/about')
                    ? 'text-accent-500'
                    : scrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-950'
                    : 'text-background-50/80 hover:text-background-50'
                }`}
              >
                关于我们
              </Link>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <button className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors">
              <i
                className={`ri-search-line ${
                  scrolled || !isHome ? 'text-foreground-700' : 'text-background-50'
                }`}
              ></i>
            </button>
            <button
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                scrolled || !isHome
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-background-50 text-foreground-950 hover:bg-background-100'
              }`}
            >
              开始阅读
              <i className="ri-arrow-right-line ml-1"></i>
            </button>
          </div>

          <button
            className="md:hidden w-9 h-9 flex items-center justify-center"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i
              className={`ri-menu-line text-xl ${
                scrolled || !isHome ? 'text-foreground-950' : 'text-background-50'
              }`}
            ></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 py-4">
            <div className="flex flex-col gap-2 px-2">
              <Link to="/" className="px-4 py-3 text-sm font-medium text-foreground-950 hover:bg-background-100 rounded-lg transition-colors">
                首页
              </Link>
              {categories.slice(1).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
              <Link to="/about" className="px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors">
                关于我们
              </Link>
              <button className="mt-2 mx-4 px-5 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium text-center transition-all hover:bg-primary-600 whitespace-nowrap">
                开始阅读
                <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}